import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { Button } from '../components/ui/Button';
import { fieldControlClass, fieldLabelClass } from '../components/ui/forms';

export function LoginPage() {
  const { status, error, login } = useAuth();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (status === 'authenticated') {
    const from = (location.state as { from?: Location })?.from;
    return <Navigate to={from?.pathname || '/dashboard'} replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(username, password);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-ink px-4 py-10">
      <div className="pointer-events-none absolute inset-0 bg-radial-gold" aria-hidden="true" />
      <div className="pointer-events-none absolute -top-24 left-1/2 h-72 w-[36rem] -translate-x-1/2 rounded-full bg-gold-700/10 blur-3xl" aria-hidden="true" />

      <form
        onSubmit={handleSubmit}
        className="relative w-full max-w-sm space-y-5 rounded-card border border-ink-border bg-panel-gradient p-7 shadow-card sm:p-8"
      >
        <div className="flex flex-col items-center text-center">
          <img src="/ps-logo.webp" alt="Portable Services LLC" className="h-20 w-20 rounded-full object-contain drop-shadow-[0_8px_20px_rgba(0,0,0,0.5)]" />
          <h1 className="mt-4 text-lg font-extrabold uppercase tracking-wide text-white">Portable Services</h1>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-gold-500">Operations Portal</p>
          <p className="mt-3 text-xs text-slate-500">Sign in with your company username.</p>
        </div>

        <div className="space-y-1.5">
          <label htmlFor="username" className={fieldLabelClass}>
            Username
          </label>
          <input
            id="username"
            name="username"
            autoComplete="username"
            className={fieldControlClass}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
        </div>

        <div className="space-y-1.5">
          <label htmlFor="password" className={fieldLabelClass}>
            Password
          </label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            className={fieldControlClass}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>

        {error && (
          <p role="alert" className="rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-red-200">
            {error}
          </p>
        )}

        <Button type="submit" busy={submitting} busyLabel="Signing in…" className="w-full">
          Sign in
        </Button>

        <p className="text-center text-[10px] uppercase tracking-widest text-slate-600">Licensed &amp; Insured · Home Improvement</p>
      </form>
    </div>
  );
}
